import {
  ActionIcon,
  Anchor,
  AppShell,
  Badge,
  Box,
  Divider,
  Flex,
  MantineSize,
  Overlay,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconChevronLeft,
  IconChevronRight,
  IconProps,
  IconX,
} from '@tabler/icons-react';
import { t } from 'i18next';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavProvider } from '../../../provider/navigationContext.tsx';
import PrivacyModal from '../PrivacyModal/PrivacyModal.tsx';
import SupportModal from '../SupportModal/SupportModal.tsx';
import TermsModal from '../TermsModal/TermsModal.tsx';
import classes from './SideBar.module.less';

interface NavbarLinkProps {
  icon: React.ForwardRefExoticComponent<
    IconProps & React.RefAttributes<SVGSVGElement>
  >;
  label: string;
  active?: boolean;
  onClick?: () => void;
  opened: boolean;
  isSubMenuTitle?: boolean;
  isSubMenuItem?: boolean;
  subMenuItems?: { id: string; onClickSubMenu?: () => void }[]; // Add submenu items
  disabled?: boolean;
  size?: MantineSize;
  tooltipLabel?: string;
  badge?: number;
}

function NavbarLink({
  icon: Icon,
  label,
  active,
  onClick,
  opened,
  isSubMenuTitle,
  isSubMenuItem,
  disabled,
  size = 'lg',
  tooltipLabel,
  badge,
}: NavbarLinkProps) {
  return (
    <Tooltip
      hidden={opened}
      label={tooltipLabel || label}
      position="right"
      transitionProps={{ duration: 0 }}
    >
      <UnstyledButton
        onClick={onClick}
        className={classes.link}
        data-active={(!isSubMenuTitle && active) || undefined}
        p={'sm'}
        w={opened ? 266 : 48}
        style={{
          borderRadius: '4px',
          transition: 'width 50ms ease-in-out',
          cursor: disabled ? 'not-allowed' : 'pointer',
          position: 'relative',
        }}
        disabled={disabled}
      >
        <Flex align={'end'} gap={'sm'} style={{ overflow: 'visible', flex: 1 }}>
          <Box pos="relative" style={{ flexShrink: 0, lineHeight: 0 }}>
            <Icon
              className={classes.linkIcon}
              style={{
                opacity: disabled ? 0.6 : 1,
                color: disabled ? 'rgba(255, 255, 255, 0.6)' : undefined,
              }}
            />
            {badge != null && badge > 0 && (
              <Badge
                size="xs"
                circle
                color="red"
                style={{ position: 'absolute', top: -6, right: -8 }}
              >
                {badge > 99 ? '99+' : badge}
              </Badge>
            )}
          </Box>
          <Text
            lh={'21px'}
            style={{
              overflow: 'hidden',
              lineClamp: 1,
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              opacity: disabled ? 0.75 : 1,
              color: disabled ? 'rgba(255, 255, 255, 0.75)' : undefined,
            }}
            hidden={!opened}
            fw={isSubMenuItem ? 400 : 600}
            size={size}
            ml={isSubMenuItem ? 'xl' : 0}
          >
            {label}
          </Text>
        </Flex>
      </UnstyledButton>
    </Tooltip>
  );
}

interface INavbarProps {
  tabs: ITabsProps[];
  route?: string;
  disabled?: boolean;
}

export interface ITabsProps {
  ids: string[];
  title: string;
  nextButtonLabel?: string;

  icon: React.ForwardRefExoticComponent<IconProps & React.RefAttributes<any>>;
  withTitle?: boolean;
  badge?: number;
  featureCode?: string;
  disabled?: boolean;
  tooltipLabel?: string;
}

export const Navbar: React.FC<INavbarProps> = inject()(
  observer(({ tabs, route, disabled }) => {
    const { opened, open, close } = useNavProvider();
    const navigate = useNavigate();
    const location = useLocation();

    // Detect mobile devices
    const isSmallScreen = useMediaQuery('(max-width: 767px)');
    const isPortrait = useMediaQuery('(orientation: portrait)');
    const isLandscape = useMediaQuery('(orientation: landscape)');
    const isTouchDevice = useMediaQuery('(pointer: coarse)');
    const isMobile = isSmallScreen && isPortrait && isTouchDevice;
    const isMobileLandscape = isSmallScreen && isLandscape && isTouchDevice;

    const [openedTerms, { open: openTerms, close: closeTerms }] =
      useDisclosure(false);
    const [openedPrivacy, { open: openPrivacy, close: closePrivacy }] =
      useDisclosure(false);
    const [openedSupport, { open: openSupport, close: closeSupport }] =
      useDisclosure(false);

    const segments = location.pathname.split('/').filter(Boolean);
    const secondSegment = segments[1];

    const links = tabs.map((item) => {
      const linkDisabled = disabled || item.disabled;
      return (
        <div key={item.ids[0]}>
          <NavbarLink
            {...item}
            active={item.ids?.includes(secondSegment)}
            onClick={() => {
              if (!linkDisabled) {
                navigate(`/${route}/${item.ids[0]}`);
                if (isMobile) close();
              }
            }}
            label={item.title || ''}
            opened={opened}
            disabled={linkDisabled}
            badge={item.badge}
          />
        </div>
      );
    });

    // On mobile portrait, hide sidebar completely when closed (hamburger menu in header opens it)
    if (isMobile && !opened) {
      return (
        <>
          <TermsModal onClose={closeTerms} opened={openedTerms} />
          <PrivacyModal onClose={closePrivacy} opened={openedPrivacy} />
          <SupportModal onClose={closeSupport} opened={openedSupport} />
        </>
      );
    }

    return (
      <>
        {/* Mobile overlay backdrop when menu is open - only for portrait mode */}
        {isMobile && opened && (
          <Overlay
            color="rgba(0, 0, 0, 0.3)"
            onClick={close}
            style={{
              position: 'fixed',
              top: 'calc(60px + var(--safe-area-inset-top, 0px))',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
          />
        )}
        <AppShell.Navbar
          bg={'var(--mantine-color-blue-7)'}
          className={`${classes.navbar} ${
            isMobile && opened
              ? classes.mobileOverlay
              : isMobileLandscape
                ? classes.mobileLandscape
                : ''
          }`}
        >
          <TermsModal onClose={closeTerms} opened={openedTerms} />
          <PrivacyModal onClose={closePrivacy} opened={openedPrivacy} />
          <SupportModal onClose={closeSupport} opened={openedSupport} />
          {/* Hide the toggle chevron on mobile - hamburger in header replaces it */}
          <ActionIcon
            variant={'outline'}
            pos={'absolute'}
            right={opened ? -12 : -12}
            top={8}
            size={'sm'}
            radius={'xl'}
            onClick={() => (opened ? close() : open())}
            hidden={isMobile}
            style={{
              zIndex: 1001,
              backgroundColor: 'var(--mantine-color-white)',
              color: 'var(--mantine-color-blue-7)',
              border: '1px solid var(--mantine-color-blue-7)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            {opened ? (
              <IconChevronLeft size={16} />
            ) : (
              <IconChevronRight size={16} />
            )}
          </ActionIcon>
          <Stack
            gap={'xs'}
            className={classes.navbarMain}
            pl={'sm'}
            pr={'sm'}
            mt={isMobile ? 'sm' : 40}
          >
            <Box
              mb={'xs'}
              hidden={!location.pathname.includes('hcpPatientDashboard')}
            >
              <Tooltip label={t('navbar.close')}>
                <NavbarLink
                  onClick={() => {
                    if (!disabled) navigate('/hcpDashboard');
                  }}
                  label={t('navbar.close')}
                  icon={IconX}
                  opened={opened}
                  size={'xl'}
                  disabled={disabled}
                />
              </Tooltip>
              <Divider mt={'sm'} />
            </Box>
            <Stack
              gap={'sm'}
              pl={
                location.pathname.includes('hcpPatientDashboard') && opened
                  ? 'sm'
                  : 0
              }
              style={{ flex: 1 }}
            >
              {links}
            </Stack>
          </Stack>
          {opened && (
            <Stack
              className={classes.footer}
              w={'100%'}
              style={{
                marginTop: 'auto',
                flexShrink: 0,
              }}
            >
              <Stack pl={'md'} pr={'md'}>
                <Anchor onClick={openSupport} c={'white'} size={'xs'}>
                  {t('navbar.support')}
                </Anchor>
                <Anchor onClick={openTerms} c={'white'} size={'xs'}>
                  {t('navbar.terms')}
                </Anchor>
                <Anchor onClick={openPrivacy} c={'white'} size={'xs'}>
                  {t('navbar.privacyPolicy')}
                </Anchor>
              </Stack>
              <Flex
                pt={'md'}
                pl={'md'}
                pr={'md'}
                pb={'calc(var(--mantine-spacing-md) + var(--safe-area-inset-bottom, 0px))'}
                className={classes.version}
                mih={46}
                justify={'space-between'}
              >
                <Text size={'xs'}>Appmed™</Text>
                <Tooltip label={__GIT_COMMIT_HASH__} withArrow openDelay={1500}>
                  <Text size={'xs'} style={{ cursor: 'default' }}>
                    v.2.0
                  </Text>
                </Tooltip>
              </Flex>
            </Stack>
          )}
        </AppShell.Navbar>
      </>
    );
  }),
);
